"use client"

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Printer, Download, Settings, Eye, EyeOff, Edit3, Save, Type, CheckCircle, AlertCircle } from 'lucide-react'
import { LabelGenerator } from './LabelGenerator'
import type { 
  LabelPrinterProps, 
  LabelField, 
  LabelGeneratorConfig,
  LabelExportOptions 
} from './types'

// Configuración por defecto
const DEFAULT_CONFIG: LabelGeneratorConfig = {
  width: 90,      // mm
  height: 29,   // mm
  dpi: 300,       // DPI
  margin: 0,      // mm
  contentScale: 0.82 // 100% escala por defecto
}

const LabelPrinter: React.FC<LabelPrinterProps> = ({
  fields = [],
  fixedField,
  category,
  size,
  onPrint,
  onMarkPrinted,
  onBeforePrint,
  onExport,
  showControls = true,
  allowEdit = false,
  className = '',
  debug = false,
  onFieldSave,
  fieldSaving = {},
  hasFieldChanged,
  onFieldChange,
  onFixedFieldChange,
  kioskMode = false,
  alreadyPrinted = false,
  footer,
  isGenericLabel
}) => {
  // Estados
  const [localFields, setLocalFields] = useState<LabelField[]>(() => {
    // Initialize fields with default titleCase values
    return fields.map((field, index) => ({
      ...field,
      titleCase: field.titleCase !== undefined ? field.titleCase : (
        index === 2 ? true : // 3rd field (index 2) = true
        index === 3 ? false : // 4th field (index 3) = false  
        index >= 2 // Other fields from 3rd onwards = true by default
      )
    }))
  })
  const [localFixedField, setLocalFixedField] = useState(fixedField)
  const [fixedFieldVisible, setFixedFieldVisible] = useState(true)
  const [fieldVisibility, setFieldVisibility] = useState<Record<string, boolean>>(() => {
    const visibility: Record<string, boolean> = {}
    fields.forEach(field => {
      visibility[field.name] = true // Por defecto todos visibles
    })
    return visibility
  })
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('')
  const [qrImageUrl, setQrImageUrl] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showPreview, setShowPreview] = useState(false) // Vista previa oculta por defecto
  const [labelConfig, setLabelConfig] = useState<LabelGeneratorConfig>({
    ...DEFAULT_CONFIG,
    ...size,
    dpi: size?.dpi || DEFAULT_CONFIG.dpi,
    margin: size?.margin || DEFAULT_CONFIG.margin,
    contentScale: size?.contentScale || DEFAULT_CONFIG.contentScale
  })

  // Referencias
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const generatorRef = useRef<LabelGenerator | null>(null)

  // Inicializar generador
  useEffect(() => {
    if (canvasRef.current) {
      generatorRef.current = new LabelGenerator(canvasRef.current, labelConfig)
      if (isGenericLabel) {
          setShowPreview(true)
        }
    }
  }, [labelConfig])

  // Generar QR si hay campo fijo
  useEffect(() => {
    const generateQR = async () => {
      if (localFixedField?.value?.trim()) {
        try {
          const qrUrl = await LabelGenerator.generateQRCode(localFixedField.value)
          setQrImageUrl(qrUrl)
        } catch (error) {
          console.error('Error generando QR:', error)
          setQrImageUrl('')
        }
      } else {
        setQrImageUrl('')
      }
    }

    generateQR()
  }, [localFixedField])

  // Generar vista previa
  const generatePreview = useCallback(async () => {
    if (!generatorRef.current) return

    try {
      setLoading(true)
      
      // Filtrar campos visibles
      const visibleFieldsForLayout = localFields.filter(field => fieldVisibility[field.name] !== false)
      const visibleFixedField = fixedFieldVisible ? localFixedField : undefined
      
      // Determinar layout con campos visibles
      const layout = LabelGenerator.determineLayout(visibleFieldsForLayout, visibleFixedField)
      
      if (debug) {
        console.log('🎨 Layout determinado:', layout)
        console.log('👁️ Campos visibles:', visibleFieldsForLayout.map(f => f.name))
        console.log('👁️ QR visible:', fixedFieldVisible)
      }

      // Generar etiqueta
      const imageUrl = await generatorRef.current.generateLabel(layout, fixedFieldVisible ? qrImageUrl : '')
      setPreviewImageUrl(imageUrl)

      if (debug) {
        console.log('🖼️ Vista previa generada:', imageUrl.substring(0, 50) + '...')
      }
    } catch (error) {
      console.error('Error generando vista previa:', error)
    } finally {
      setLoading(false)
    }
  }, [localFields, localFixedField, qrImageUrl, debug, fieldVisibility, fixedFieldVisible])

  // Regenerar vista previa cuando cambien los datos
  useEffect(() => {
    if (generatorRef.current) {
      generatePreview()
    }
  }, [generatePreview])

  // Actualizar campo local
  const updateField = (fieldName: string, newValue: string) => {
    setLocalFields(prev => 
      prev.map(field => 
        field.name === fieldName 
          ? { ...field, value: newValue }
          : field
      )
    )
    // Notificar al componente padre del cambio
    onFieldChange?.(fieldName, newValue)
  }

  // Actualizar campo fijo
  const updateFixedField = (newValue: string) => {
    setLocalFixedField(prev => prev ? { ...prev, value: newValue } : undefined)
    // Notificar al componente padre del cambio
    onFixedFieldChange?.(newValue)
  }

  // Toggle visibilidad de campo
  const toggleFieldVisibility = (fieldName: string) => {
    setFieldVisibility(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }))
  }

  // Toggle visibilidad del campo QR
  const toggleFixedFieldVisibility = () => {
    setFixedFieldVisible(!fixedFieldVisible)
  }

  // Toggle title case de campo
  const toggleFieldTitleCase = (fieldName: string) => {
    setLocalFields(prev => 
      prev.map(field => 
        field.name === fieldName 
          ? { ...field, titleCase: !field.titleCase }
          : field
      )
    )
  }

  // Estado de carga para "Marcar Impreso"
  const [markingPrinted, setMarkingPrinted] = useState(false)

  // Manejar "Marcar Impreso": llama al endpoint que actualiza impreso=1 en BD.
  const handleMarkPrinted = useCallback(async () => {
    if (!onMarkPrinted) return
    try {
      setMarkingPrinted(true)
      await onMarkPrinted()
    } catch (err) {
      console.error('onMarkPrinted lanzó un error:', err)
    } finally {
      setMarkingPrinted(false)
    }
  }, [onMarkPrinted])

  // Manejar impresión: delega al consumidor vía onPrint (que envía al endpoint).
  // Ya no se abre el diálogo de impresión del sistema.
  const handlePrint = useCallback(async () => {
    if (!previewImageUrl) return

    // Permitir al consumidor abortar/confirmar antes de imprimir
    if (onBeforePrint) {
      try {
        const proceed = await onBeforePrint()
        if (!proceed) return
      } catch (err) {
        console.error('onBeforePrint lanzó un error, se cancela la impresión:', err)
        return
      }
    }

    onPrint?.()
  }, [previewImageUrl, onPrint, onBeforePrint])

  // Manejar exportación
  const handleExport = useCallback((options: LabelExportOptions = { format: 'png' }) => {
    if (generatorRef.current && previewImageUrl) {
      const exportUrl = generatorRef.current.exportLabel(options)
      
      // Crear enlace de descarga
      const link = document.createElement('a')
      link.href = exportUrl
      link.download = `etiqueta.${options.format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      onExport?.(exportUrl)
    }
  }, [previewImageUrl, onExport])

  // Campos visibles (con valor)
  const visibleFields = localFields.filter(field => field.value?.trim())

  // Calcular campos vacíos (requeridos o no)
  const emptyFields = localFields.filter(field => 
    !field.value || !field.value.trim()
  )
  const hasEmptyFields = emptyFields.length > 0

  return (
    <div className={`label-printer ${className}`}>
      {/* Canvas oculto para generación */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div className={`grid gap-6 ${(showControls && !kioskMode) ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Panel de configuración - SOLO CUANDO showControls=true Y NO kioskMode */}
        {showControls && !kioskMode && (
          <div className="space-y-4">
            {/* Header con controles */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Configuración de Etiqueta
                </h3>
                {hasEmptyFields && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                    <span className="text-xs font-medium text-amber-700">
                      {emptyFields.length} {emptyFields.length === 1 ? 'campo vacío' : 'campos vacíos'}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Configurar tamaño"
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>

            {/* Configuración de tamaño */}
            {showSettings && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Tamaño de Etiqueta</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Ancho (mm)</label>
                    <input
                      type="number"
                      value={labelConfig.width}
                      onChange={(e) => setLabelConfig(prev => ({ ...prev, width: Number(e.target.value) }))}
                      className="w-full px-2 py-1 text-sm border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Alto (mm)</label>
                    <input
                      type="number"
                      value={labelConfig.height}
                      onChange={(e) => setLabelConfig(prev => ({ ...prev, height: Number(e.target.value) }))}
                      className="w-full px-2 py-1 text-sm border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">DPI</label>
                    <input
                      type="number"
                      value={labelConfig.dpi}
                      onChange={(e) => setLabelConfig(prev => ({ ...prev, dpi: Number(e.target.value) }))}
                      className="w-full px-2 py-1 text-sm border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Margen (mm)</label>
                    <input
                      type="number"
                      value={labelConfig.margin}
                      onChange={(e) => setLabelConfig(prev => ({ ...prev, margin: Number(e.target.value) }))}
                      className="w-full px-2 py-1 text-sm border rounded"
                    />
                  </div>
                </div>
                
                {/* Control de escala del contenido */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">Escala del Contenido</h5>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-600">Tamaño interno</label>
                      <span className="text-xs text-gray-500">
                        {Math.round((labelConfig.contentScale || 1.0) * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.3"
                      max="1.0"
                      step="0.05"
                      value={labelConfig.contentScale || 1.0}
                      onChange={(e) => setLabelConfig(prev => ({ ...prev, contentScale: Number(e.target.value) }))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>30%</span>
                      <span>100%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Reduce el tamaño de todo el contenido manteniendo el diseño centrado
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Campo fijo (QR) */}
            {localFixedField && (
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={toggleFixedFieldVisibility}
                      className={`p-1 rounded ${
                        fixedFieldVisible
                          ? "text-green-600 hover:text-green-700" 
                          : "text-gray-400 hover:text-gray-500"
                      }`}
                      title={fixedFieldVisible ? "Ocultar QR" : "Mostrar QR"}
                    >
                      {fixedFieldVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <span className="text-sm font-medium">
                      {localFixedField.label || 'Código QR'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">Campo QR</div>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={localFixedField.value}
                    onChange={(e) => updateFixedField(e.target.value)}
                    disabled={!allowEdit || (localFixedField.editable === false)}
                    className={`flex-1 px-2 py-1 text-sm border rounded ${
                      (allowEdit && localFixedField.editable !== false)
                        ? `border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                            hasFieldChanged && hasFieldChanged('qr_field', localFixedField.value) ? 'border-yellow-400 bg-yellow-50' : ''
                          }` 
                        : "border-gray-200 bg-gray-50 text-gray-500"
                    }`}
                    placeholder="Valor para código QR"
                  />
                  
                  {/* Botón de guardado para campo QR en usuarios reales */}
                  {onFieldSave && allowEdit && localFixedField.editable !== false && (
                    <button
                      onClick={() => onFieldSave('qr_field', localFixedField.value)}
                      disabled={fieldSaving['qr_field'] || !(hasFieldChanged && hasFieldChanged('qr_field', localFixedField.value))}
                      className={`p-1.5 backdrop-blur-sm border rounded-xl transition-all duration-300 min-w-[32px] min-h-[32px] flex items-center justify-center ${
                        hasFieldChanged && hasFieldChanged('qr_field', localFixedField.value)
                          ? "bg-green-600/20 border-green-400/30 text-green-800 hover:bg-green-600/30"
                          : "bg-gray-500/10 border-gray-300/20 text-gray-400"
                      }`}
                      title={hasFieldChanged && hasFieldChanged('qr_field', localFixedField.value) ? "Guardar cambios en QR" : "Sin cambios"}
                    >
                      {fieldSaving['qr_field'] ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                    </button>
                  )}
                </div>
                {localFixedField.editable === false && (
                  <p className="text-xs text-gray-500 mt-1">
                    Campo fijo - No editable
                  </p>
                )}
              </div>
            )}

            {/* Campos configurables */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Campos de la Etiqueta</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {localFields.map((field) => (
                  <div key={field.name} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleFieldVisibility(field.name)}
                          className={`p-1 rounded ${
                            fieldVisibility[field.name]
                              ? "text-green-600 hover:text-green-700" 
                              : "text-gray-400 hover:text-gray-500"
                          }`}
                          title={fieldVisibility[field.name] ? "Ocultar campo" : "Mostrar campo"}
                        >
                          {fieldVisibility[field.name] ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        
                        {/* Title Case Toggle Button - Only for editable fields */}
                        {allowEdit && field.editable !== false && (
                          <button
                            onClick={() => toggleFieldTitleCase(field.name)}
                            className={`p-1 rounded ${
                              field.titleCase
                                ? "text-blue-600 hover:text-blue-700" 
                                : "text-gray-400 hover:text-gray-500"
                            }`}
                            title={field.titleCase ? "Desactivar Title Case" : "Activar Title Case"}
                          >
                            <Type className="h-4 w-4" />
                          </button>
                        )}
                        
                        <span className="text-sm font-medium">{field.label}</span>
                        
                        {/* Title Case Status Indicator */}
                        {field.titleCase && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                            Aa
                          </span>
                        )}
                      </div>
                      {allowEdit && (
                        <Edit3 className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                    
                    {field.editable !== false && (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          {/* Renderizar campo según tipo */}
                          {field.type === 'enum' ? (
                            <select
                              value={field.value}
                              onChange={(e) => updateField(field.name, e.target.value)}
                              disabled={!allowEdit}
                              className={`flex-1 px-2 py-1 text-sm border rounded ${
                                allowEdit 
                                  ? (() => {
                                      const isEmpty = !field.value || String(field.value).trim() === ''
                                      const hasChanged = hasFieldChanged && hasFieldChanged(field.name, field.value)
                                      const isValidOption = field.options?.includes(field.value) || isEmpty
                                      
                                      if (hasChanged) return 'border-yellow-400 bg-yellow-50'
                                      if (!isValidOption) return 'border-red-400 bg-red-50'
                                      if (isEmpty) return 'border-amber-400 bg-amber-50'
                                      return 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                                    })()
                                  : "border-gray-200 bg-gray-50 text-gray-500"
                              }`}
                            >
                              <option value="">Seleccionar...</option>
                              {field.options?.map((option: string) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : field.type === 'number' ? (
                            <input
                              type="number"
                              value={field.value}
                              onChange={(e) => updateField(field.name, e.target.value)}
                              disabled={!allowEdit}
                              className={`flex-1 px-2 py-1 text-sm border rounded ${
                                allowEdit 
                                  ? (() => {
                                      const isEmpty = !field.value || String(field.value).trim() === ''
                                      const hasChanged = hasFieldChanged && hasFieldChanged(field.name, field.value)
                                      
                                      if (hasChanged) return 'border-yellow-400 bg-yellow-50'
                                      if (isEmpty) return 'border-amber-400 bg-amber-50'
                                      return 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                                    })()
                                  : "border-gray-200 bg-gray-50 text-gray-500"
                              }`}
                              placeholder={`Valor para ${field.label.toLowerCase()}`}
                            />
                          ) : (
                            <input
                              type={field.name === 'email' ? 'email' : field.name === 'telefono' ? 'tel' : 'text'}
                              value={field.value}
                              onChange={(e) => updateField(field.name, e.target.value)}
                              disabled={!allowEdit}
                              className={`flex-1 px-2 py-1 text-sm border rounded ${
                                allowEdit 
                                  ? (() => {
                                      const isEmpty = !field.value || String(field.value).trim() === ''
                                      const hasChanged = hasFieldChanged && hasFieldChanged(field.name, field.value)
                                      
                                      if (hasChanged) return 'border-yellow-400 bg-yellow-50'
                                      if (isEmpty) return 'border-amber-400 bg-amber-50'
                                      return 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                                    })()
                                  : "border-gray-200 bg-gray-50 text-gray-500"
                              }`}
                              placeholder={`Valor para ${field.label.toLowerCase()}`}
                            />
                          )}
                          
                          {/* Botón de guardado individual */}
                          {onFieldSave && allowEdit && (
                            <button
                              onClick={() => onFieldSave(field.name, field.value)}
                              disabled={fieldSaving[field.name] || !(hasFieldChanged && hasFieldChanged(field.name, field.value))}
                              className={`p-1.5 backdrop-blur-sm border rounded-xl transition-all duration-300 min-w-[32px] min-h-[32px] flex items-center justify-center ${
                                hasFieldChanged && hasFieldChanged(field.name, field.value)
                                  ? "bg-green-600/20 border-green-400/30 text-green-800 hover:bg-green-600/30"
                                  : "bg-gray-500/10 border-gray-300/20 text-gray-400"
                              }`}
                              title={hasFieldChanged && hasFieldChanged(field.name, field.value) ? `Guardar cambios en ${field.label}` : "Sin cambios"}
                            >
                              {fieldSaving[field.name] ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                              ) : (
                                <Save className="h-3 w-3" />
                              )}
                            </button>
                          )}
                        </div>
                        
                        {/* Advertencias de validación solo para usuarios reales */}
                        {allowEdit && field.type === 'enum' && (() => {
                          const currentValue = String(field.value || '').trim()
                          const hasValue = currentValue !== ''
                          const validOptions = field.options || []
                          const isValidOption = validOptions.includes(currentValue)
                          const showWarning = hasValue && !isValidOption
                          
                          return showWarning ? (
                            <div className="p-2 bg-amber-600/10 backdrop-blur-sm border border-amber-400/20 rounded-xl">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <svg className="h-3 w-3 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="ml-2">
                                  <p className="text-xs text-amber-700">
                                    <strong>Valor no válido:</strong> "{currentValue}" no coincide con las opciones predeterminadas.
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : null
                        })()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Vista previa y acciones */}
        <div className="space-y-4">
          {/* Título + botón toggle solo cuando showControls=true Y NO kioskMode */}
          {showControls && !kioskMode && (
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">Vista Previa</h4>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title={showPreview ? "Ocultar vista previa" : "Mostrar vista previa"}
              >
                {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showPreview ? "Ocultar" : "Mostrar"}
              </button>
            </div>
          )}

          {/* Vista previa de la etiqueta */}
          {showPreview && <div className="border-2 border-dashed border-gray-300 p-4 flex items-center justify-center min-h-[200px]">
            {loading ? (
              <div className="text-gray-500 flex items-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
                <span>Generando vista previa...</span>
              </div>
            ) : previewImageUrl ? (
              <div className="relative">
                <img 
                  src={previewImageUrl}
                  alt="Vista previa de etiqueta"
                  style={{
                    maxWidth: '500px',
                    maxHeight: '250px',
                    objectFit: 'contain'
                  }}
                />
                {/* Watermark de categoría */}
                {category && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    style={{
                      transform: 'rotate(-45deg)',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: 'rgba(107, 114, 128, 0.3)',
                      textTransform: 'uppercase',
                      letterSpacing: '2px',
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    }}
                  >
                    {category}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500">
                {visibleFields.length === 0 ? 'Agregue campos para ver la vista previa' : 'Generando vista previa...'}
              </div>
            )}
          </div>}

          {/* Información de la etiqueta - solo en debug y showControls Y NO kioskMode */}
          {debug && showControls && !kioskMode && (
            <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
              <p><strong>Tamaño:</strong> {labelConfig.width}×{labelConfig.height}mm</p>
              <p><strong>DPI:</strong> {labelConfig.dpi}</p>
              <p><strong>Campos visibles:</strong> {visibleFields.length}</p>
              <p><strong>Tiene QR:</strong> {localFixedField?.value ? 'Sí' : 'No'}</p>
            </div>
          )}

          {/* Footer slot (REQ-003) */}
          {footer && <div className="mt-4">{footer}</div>}

          {/* Botones de acción */}
          {showControls && (
            <div className={`flex space-x-3 ${kioskMode ? 'justify-center' : ''}`}>
              {/* Botón 1: Marcar Impreso (opcional, solo si onMarkPrinted) */}
              {onMarkPrinted && (
                <button
                  onClick={handleMarkPrinted}
                  disabled={markingPrinted || loading}
                  className={`inline-flex items-center px-4 py-2 bg-amber-600/20 backdrop-blur-sm border border-amber-400/30 text-amber-800 text-sm font-medium rounded-xl hover:bg-amber-600/30 hover:border-amber-500/40 hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300 disabled:opacity-50 ${
                    kioskMode ? 'px-8 py-4 text-lg font-semibold' : ''
                  }`}
                  title="Marcar este gafete como impreso en la base de datos"
                >
                  {markingPrinted ? (
                    <div className={`${kioskMode ? 'h-6 w-6' : 'h-4 w-4'} mr-2 animate-spin rounded-full border-b-2 border-current`}></div>
                  ) : (
                    <CheckCircle className={`${kioskMode ? 'h-6 w-6' : 'h-4 w-4'} mr-2`} />
                  )}
                  Marcar Impreso
                </button>
              )}

              {/* Botón 2: Imprimir → servicio local de impresora */}
              {kioskMode ? (
                alreadyPrinted ? (
                  /* Botón discreto para reimpresión */
                  <button
                    onClick={handlePrint}
                    disabled={!previewImageUrl || loading}
                    className="inline-flex items-center px-6 py-3 bg-gray-500/15 backdrop-blur-sm border border-gray-300/40 text-gray-600 text-base font-medium rounded-xl hover:bg-gray-500/25 hover:border-gray-400/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Reimprimir gafete"
                  >
                    <Printer className="h-5 w-5 mr-2 opacity-70" />
                    Reimprimir
                  </button>
                ) : (
                <div className="relative flex flex-col items-center">
                  {/* Flecha animada apuntando al botón */}
                  <div className="flex flex-col items-center mb-2 animate-bounce">
                    <span className="text-green-600 font-bold text-sm tracking-wide uppercase select-none">
                      ¡Imprimir aquí!
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-8 w-8 text-green-500 drop-shadow"
                    >
                      <path d="M12 20l-8-8h5V4h6v8h5l-8 8z" />
                    </svg>
                  </div>

                  {/* Anillo pulsante de fondo */}
                  <span className="absolute inset-0 rounded-2xl bg-green-400 opacity-40 animate-ping pointer-events-none" />

                  <button
                    onClick={handlePrint}
                    disabled={!previewImageUrl || loading}
                    className="relative inline-flex items-center px-10 py-5 text-xl font-bold rounded-2xl text-white bg-green-500 hover:bg-green-600 shadow-lg shadow-green-400/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed animate-pulse"
                    title="Enviar a la impresora"
                  >
                    <Printer className="h-7 w-7 mr-3" />
                    Imprimir
                  </button>
                </div>
                )
              ) : (
                <button
                  onClick={handlePrint}
                  disabled={!previewImageUrl || loading}
                  className="inline-flex items-center px-4 py-2 bg-green-600/20 backdrop-blur-sm border border-green-400/30 text-green-800 text-sm font-medium rounded-xl hover:bg-green-600/30 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300 disabled:opacity-50"
                  title="Enviar a la impresora"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Imprimir
                </button>
              )}
              
              {/* Botón de exportar solo en modo normal (no kiosco) */}
              {!kioskMode && (
                <button
                  onClick={() => handleExport({ format: 'png' })}
                  disabled={!previewImageUrl || loading}
                  className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 text-blue-800 text-sm font-medium rounded-xl hover:bg-blue-600/30 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 disabled:opacity-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportar PNG
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contenedor oculto para impresión - siempre renderizado */}
      <div className="label-printer-print-only" style={{ display: 'none' }}>
        {previewImageUrl && (
          <img 
            src={previewImageUrl}
            alt="Etiqueta para imprimir"
          />
        )}
      </div>
    </div>
  )
}

export default LabelPrinter
//Comment
